import { useEffect, useState } from "react";
import Layout, { PageHeader, Card } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { formatNumber, formatDateShort } from "../lib/format";
import { ArrowUpRight, ArrowDownRight, Plus, Trash2, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";

const EMPTY = {
  date: new Date().toISOString().slice(0, 10),
  followers: 0, posts: 0, impressions: 0, video_views: 0,
  avg_reach: 0, avg_engagement: 0, notes: "",
};

const METRICS = [
  { key: "followers",      label: "Followers",       fmt: (v) => formatNumber(v) },
  { key: "posts",          label: "Posts",            fmt: (v) => formatNumber(v) },
  { key: "impressions",    label: "Impressions",      fmt: (v) => formatNumber(v) },
  { key: "video_views",    label: "Video views",      fmt: (v) => formatNumber(v) },
  { key: "avg_reach",      label: "Avg reach / post", fmt: (v) => formatNumber(v) },
  { key: "avg_engagement", label: "Avg engagement",   fmt: (v) => `${(v || 0).toFixed(2)}%` },
];

function Delta({ now, prev, isPercent }) {
  if (prev == null) return <span className="text-zinc-500 text-xs">First snapshot</span>;
  const d = now - prev;
  if (d === 0) return <span className="text-zinc-500 text-xs">No change</span>;
  const up = d > 0;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  const cls = up ? "text-emerald-400" : "text-red-400";
  const display = isPercent
    ? `${up ? "+" : ""}${d.toFixed(2)} pp`
    : `${up ? "+" : ""}${formatNumber(d)}`;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${cls} tabular`}>
      <Icon className="h-3.5 w-3.5" /> {display}
    </span>
  );
}

export default function Instagram() {
  const [snapshots, setSnapshots] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [metaStatus, setMetaStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null); // {ok, text}

  const reload = async () => {
    const { data } = await api.get("/instagram");
    setSnapshots(data);
  };
  const loadMeta = async () => {
    try {
      const { data } = await api.get("/instagram/meta-status");
      setMetaStatus(data);
    } catch { setMetaStatus(null); }
  };
  useEffect(() => { reload(); loadMeta(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/instagram", form);
      setForm(EMPTY); setAdding(false);
      await reload();
    } finally { setSaving(false); }
  };

  const syncMeta = async () => {
    setSyncing(true); setSyncMsg(null);
    try {
      const { data } = await api.post("/instagram/sync");
      setSyncMsg({ ok: true, text: `Synced from @${data.meta_raw?.username || "Instagram"} · ${formatNumber(data.snapshot.followers)} followers · sampled ${data.meta_raw?.media_sample || 0} posts` });
      await reload();
    } catch (e) {
      setSyncMsg({ ok: false, text: formatApiError(e.response?.data?.detail) || e.message });
    } finally {
      setSyncing(false);
      setTimeout(() => setSyncMsg(null), 8000);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this snapshot?")) return;
    await api.delete(`/instagram/${id}`);
    await reload();
  };

  // snapshots are sorted desc by date — chronologically previous is index+1
  const latest = snapshots[0];
  const prev = snapshots[1];

  return (
    <Layout>
      <PageHeader
        overline={metaStatus?.connected ? `@${metaStatus.username}` : "@indiansneakerfestival"}
        title="Instagram"
        subtitle={
          metaStatus?.connected
            ? "Connected to Meta Graph API · click Sync to pull live metrics, or log a snapshot manually."
            : "Manual snapshot tracking — each log is compared to the previous chronological entry."
        }
        actions={
          <div className="flex items-center gap-2">
            {metaStatus?.connected && (
              <button
                onClick={syncMeta}
                disabled={syncing}
                data-testid="sync-meta-btn"
                className="inline-flex items-center gap-2 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-200 disabled:opacity-50 text-sm font-medium px-4 py-2 transition-colors"
              >
                <RefreshCw className={`h-4 w-4 ${syncing ? "animate-spin" : ""}`} />
                {syncing ? "Syncing…" : "Sync from Meta"}
              </button>
            )}
            <button
              onClick={() => setAdding(true)}
              data-testid="add-snapshot-btn"
              className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium px-4 py-2 transition-colors"
            >
              <Plus className="h-4 w-4" /> Log snapshot
            </button>
          </div>
        }
        testId="instagram-header"
      />

      {/* Meta connection status */}
      {metaStatus !== null && (
        <Card className="p-4 mb-6 flex items-center justify-between gap-4" testId="meta-status-card">
          {metaStatus.connected ? (
            <>
              <div className="flex items-center gap-4 min-w-0">
                {metaStatus.profile_picture_url && (
                  <img src={metaStatus.profile_picture_url} alt="" className="h-10 w-10 border border-zinc-800" />
                )}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-400" />
                    <span className="overline">Meta connected</span>
                  </div>
                  <div className="text-sm mt-1 truncate">
                    <span className="font-medium">@{metaStatus.username}</span>
                    <span className="text-zinc-500"> · {metaStatus.name}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-6 tabular text-right text-xs text-zinc-400">
                <div><div className="overline mb-0.5">Followers</div><div className="text-white text-base font-display">{formatNumber(metaStatus.followers_count)}</div></div>
                <div><div className="overline mb-0.5">Posts</div><div className="text-white text-base font-display">{formatNumber(metaStatus.media_count)}</div></div>
              </div>
            </>
          ) : (
            <div className="flex items-center gap-3">
              <AlertCircle className="h-4 w-4 text-amber-400" />
              <div>
                <div className="text-sm">Meta not connected</div>
                <div className="text-xs text-zinc-500 mt-0.5">{metaStatus.reason || "Set META_ACCESS_TOKEN and IG_BUSINESS_ACCOUNT_ID in backend env."}</div>
              </div>
            </div>
          )}
        </Card>
      )}

      {syncMsg && (
        <div data-testid="sync-msg" className={`mb-6 px-4 py-3 text-sm border ${syncMsg.ok ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-200" : "border-red-500/30 bg-red-500/10 text-red-200"}`}>
          {syncMsg.text}
        </div>
      )}

      {adding && (
        <Card className="p-6 mb-8 border-emerald-500/40" testId="snapshot-form-card">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="overline block mb-2">Date</label>
              <input type="date" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            {METRICS.map((m) => (
              <div key={m.key}>
                <label className="overline block mb-2">{m.label}</label>
                <input type="number" step={m.key === "avg_engagement" ? "0.01" : "1"} min={0}
                  value={form[m.key]} onChange={(e) => setForm({ ...form, [m.key]: m.key === "avg_engagement" ? parseFloat(e.target.value || 0) : parseInt(e.target.value || 0) })}
                  data-testid={`input-${m.key}`}
                  className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
              </div>
            ))}
            <div className="md:col-span-3">
              <label className="overline block mb-2">Notes</label>
              <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full bg-[#0a0a0a] border border-zinc-800 px-3 py-2 text-sm focus:outline-none focus:border-emerald-500" />
            </div>
            <div className="md:col-span-3 flex items-center gap-3 pt-2">
              <button type="submit" disabled={saving} data-testid="save-snapshot"
                className="bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium px-5 py-2 transition-colors">
                {saving ? "Saving…" : "Save snapshot"}
              </button>
              <button type="button" onClick={() => { setAdding(false); setForm(EMPTY); }}
                className="px-4 py-2 text-sm border border-zinc-800 text-zinc-300 hover:bg-[#181818]">Cancel</button>
            </div>
          </form>
        </Card>
      )}

      {latest ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {METRICS.map((m) => (
            <Card key={m.key} className="p-6" testId={`metric-${m.key}`}>
              <div className="overline mb-2">{m.label}</div>
              <div className="flex items-baseline gap-3">
                <div className="font-display text-3xl md:text-4xl font-bold tabular leading-none">{m.fmt(latest[m.key])}</div>
                <Delta now={latest[m.key]} prev={prev?.[m.key]} isPercent={m.key === "avg_engagement"} />
              </div>
              <div className="text-xs text-zinc-500 mt-3 tabular">As of {formatDateShort(latest.date)}</div>
            </Card>
          ))}
        </div>
      ) : (
        <Card className="p-12 text-center border-dashed border-zinc-800 mb-10">
          <div className="font-display text-xl mb-1">No snapshots yet</div>
          <div className="text-sm text-zinc-500">Click &ldquo;Log snapshot&rdquo; to capture today&apos;s Instagram metrics.</div>
        </Card>
      )}

      <Card className="overflow-hidden" testId="history-card">
        <div className="px-6 py-4 border-b border-zinc-900">
          <div className="overline mb-1">History</div>
          <div className="font-display text-lg">{snapshots.length} snapshots</div>
        </div>
        {snapshots.length === 0 ? (
          <div className="px-6 py-10 text-center text-zinc-500 text-sm">Empty.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#181818]">
                <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                  <th className="px-4 py-3 font-medium">Date</th>
                  <th className="px-4 py-3 font-medium text-right">Followers</th>
                  <th className="px-4 py-3 font-medium text-right">Posts</th>
                  <th className="px-4 py-3 font-medium text-right">Impressions</th>
                  <th className="px-4 py-3 font-medium text-right">Video views</th>
                  <th className="px-4 py-3 font-medium text-right">Avg reach</th>
                  <th className="px-4 py-3 font-medium text-right">Engagement</th>
                  <th className="px-4 py-3 font-medium">Notes</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map((s) => (
                  <tr key={s.id} className="border-t border-zinc-900 hover:bg-[#161616]" data-testid={`snapshot-${s.id}`}>
                    <td className="px-4 py-2.5">{formatDateShort(s.date)}</td>
                    <td className="px-4 py-2.5 text-right tabular">{formatNumber(s.followers)}</td>
                    <td className="px-4 py-2.5 text-right tabular text-zinc-400">{formatNumber(s.posts)}</td>
                    <td className="px-4 py-2.5 text-right tabular text-zinc-400">{formatNumber(s.impressions)}</td>
                    <td className="px-4 py-2.5 text-right tabular text-zinc-400">{formatNumber(s.video_views)}</td>
                    <td className="px-4 py-2.5 text-right tabular text-zinc-400">{formatNumber(s.avg_reach)}</td>
                    <td className="px-4 py-2.5 text-right tabular text-emerald-400">{(s.avg_engagement || 0).toFixed(2)}%</td>
                    <td className="px-4 py-2.5 text-zinc-500 text-xs max-w-[200px] truncate">{s.notes || "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <button onClick={() => remove(s.id)} className="p-1.5 text-red-400 hover:bg-[#1a1a1a]" data-testid={`delete-snapshot-${s.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </Layout>
  );
}
